// Single source of truth for pricing configuration
// 4-tier credit-based subscription system (Free Trial + 3 paid plans)

export const PRICING = {
  free: {
    name: "Free Trial",
    price: 0,
    annualPrice: 0,
    displayPrice: "Free",
    interval: "week" as const,
    stripePriceId: null,
    productId: null,
    credits: 0,
    rolloverCap: 0,
    description: "Limited access. Upgrade to unlock all tools.",
    tools: ["Nexis AI", "Keyword Research AI"],
  },
  starter: {
    name: "Starter",
    price: 39,
    annualPrice: 390,
    annualSavings: 78,
    displayPrice: "$39",
    interval: "month" as const,
    stripePriceId: "price_1T7iVXDwtP5fEHH1LSZWMdNB",
    annualStripePriceId: "price_1T7iVcDwtP5fEHH1pWbskqq3",
    productId: "prod_U5uKFC6qJhCxG2",
    annualProductId: "prod_U5uK1wGVtcsfGn",
    credits: 150,
    rolloverCap: 300,
    description: "For creators just starting to monetise their audience.",
    tools: ["Nexis AI", "Keyword Research AI", "Launch Advisor"],
    bonuses: [
      "Weekly 'What's Trending' email digest",
      "Access to the Isaac AI community",
    ],
  },
  creator: {
    name: "Creator",
    price: 69,
    annualPrice: 690,
    annualSavings: 138,
    displayPrice: "$69",
    interval: "month" as const,
    stripePriceId: "price_1T7iVZDwtP5fEHH1KDqhkrwY",
    annualStripePriceId: "price_1T7iVdDwtP5fEHH1DxW96Y1L",
    productId: "prod_U5uKNMuW631vzc",
    annualProductId: "prod_U5uKmCH72GUM0l",
    credits: 600,
    rolloverCap: 1200,
    description: "For creators actively building and selling digital products.",
    popular: true,
    tools: ["Nexis AI", "Keyword Research AI", "Launch Advisor", "Monetisation AI", "Clipping AI"],
    bonuses: [
      "Monthly content calendar template",
      "Priority email support (24hr response)",
      "Monthly group Q&A call",
      "Viral hook library (updated monthly)",
    ],
  },
  pro: {
    name: "Pro",
    price: 99,
    annualPrice: 990,
    annualSavings: 198,
    displayPrice: "$99",
    interval: "month" as const,
    stripePriceId: "price_1T7iVaDwtP5fEHH1YzFMHIoL",
    annualStripePriceId: "price_1T7iVeDwtP5fEHH17c4f144q",
    productId: "prod_U5uKawtQzjlOd9",
    annualProductId: "prod_U5uKRAASzbPUCX",
    credits: 2000,
    rolloverCap: 4000,
    description: "For full-time creators and agencies scaling their income.",
    tools: ["Nexis AI", "Keyword Research AI", "Launch Advisor", "Monetisation AI", "Clipping AI", "Email List Builder AI", "Sales Page Builder AI"],
    bonuses: [
      "Priority rendering queue for Clipping AI",
      "Weekly personalised trending opportunity report",
      "1-on-1 product review per month",
      "Early access to all new features",
      "Private Pro community channel",
    ],
  },
} as const;

export type PlanTier = "free" | "starter" | "creator" | "pro";

// Credit costs per action
export const CREDIT_COSTS = {
  "generate-script": 2,
  "generate-title": 2,
  "generate-description": 2,
  "ai-script-edit": 2,
  "generate-image-prompts": 2,
  "generate-video-prompts": 2,
  "clip-analysis": 3,
  "monetization-generate-products": 5,
  "monetization-build-chapter": 5,
  "keyword-search": 0,
  "askfint-chat": 1,
  "email-list-builder": 4,
  "sales-page-builder": 4,
  "launch-advisor": 2,
} as const;

export type CreditAction = keyof typeof CREDIT_COSTS;

// All valid price IDs for entitlement checks (includes legacy prices)
export const ALL_VALID_PRICE_IDS = [
  PRICING.starter.stripePriceId,
  PRICING.creator.stripePriceId,
  PRICING.pro.stripePriceId,
  PRICING.starter.annualStripePriceId,
  PRICING.creator.annualStripePriceId,
  PRICING.pro.annualStripePriceId,
  // Legacy prices - still valid for existing subscribers (grandfathered to Pro)
  "price_1T7gnGDwtP5fEHH1atADSwjn", // Old Starter $29
  "price_1T7gnHDwtP5fEHH1etqxSp2d", // Old Creator $59
  "price_1T7gnIDwtP5fEHH1fdjGuOae", // Old Pro $79
  "price_1SjNCSDwtP5fEHH19gUsTnIc", // Old $99/month
  "price_1SqzBrDwtP5fEHH1W4kDg1An", // Old Lifetime $899
  "price_1Sia0oDwtP5fEHH1SxJqroMT", // Old $69/month
  "price_1ScBv8DwtP5fEHH1S5GHMo2I", // Old yearly
  "price_1ScBuvDwtP5fEHH1HC7Qtsai", // Old $99/month
] as const;

// All valid product IDs for entitlement checks
export const ALL_VALID_PRODUCT_IDS = [
  PRICING.starter.productId,
  PRICING.creator.productId,
  PRICING.pro.productId,
  PRICING.starter.annualProductId,
  PRICING.creator.annualProductId,
  PRICING.pro.annualProductId,
  // Legacy product IDs (grandfathered to Pro)
  "prod_U5sYzXO0E0Y39x", // Old Starter $29
  "prod_U5sYAqeax0HzND", // Old Creator $59
  "prod_U5sYHGQoO8cqA1", // Old Pro $79
  "prod_TgkhHGZ32TniZb", // Old Monthly $99
  "prod_TocQmCkbfkKUlN", // Old Lifetime $899
  "prod_Tgkhkn7AyXBPM4", // Old Yearly $999
  "prod_TfvsuzHJF7Hvue", // Monthly $69
  "prod_TZKagu7JSUhfq8", // Yearly
  "prod_TZKacLRfh0CGmK", // Old Monthly
] as const;

// Legacy product IDs that map to Pro tier
export const LEGACY_PRODUCT_IDS = [
  "prod_U5sYzXO0E0Y39x",
  "prod_U5sYAqeax0HzND",
  "prod_U5sYHGQoO8cqA1",
  "prod_TgkhHGZ32TniZb",
  "prod_Tgkhkn7AyXBPM4",
  "prod_TfvsuzHJF7Hvue",
  "prod_TZKagu7JSUhfq8",
  "prod_TZKacLRfh0CGmK",
] as const;

// Lifetime product ID (unlimited credits)
export const LIFETIME_PRODUCT_ID = "prod_TocQmCkbfkKUlN";

// Helper to format price display
export const formatPrice = (price: number, interval: "month" | "year" | "week" | "lifetime"): string => {
  if (interval === "lifetime") return `$${price}`;
  if (interval === "week") return "Free";
  return `$${price}/${interval === "month" ? "mo" : "yr"}`;
};

// Get plan tier from product ID
export const getPlanTier = (productId: string | null): PlanTier | "lifetime" | null => {
  if (!productId) return null;
  if (productId === LIFETIME_PRODUCT_ID) return "lifetime";
  if (productId === PRICING.starter.productId || productId === PRICING.starter.annualProductId) return "starter";
  if (productId === PRICING.creator.productId || productId === PRICING.creator.annualProductId) return "creator";
  if (productId === PRICING.pro.productId || productId === PRICING.pro.annualProductId) return "pro";
  // Legacy products map to Pro
  if ((LEGACY_PRODUCT_IDS as readonly string[]).includes(productId)) return "pro";
  return null;
};

// Get plan name from product ID
export const getPlanName = (productId: string | null): string => {
  const tier = getPlanTier(productId);
  if (!tier) return "None";
  if (tier === "lifetime") return "Lifetime";
  return PRICING[tier].name;
};

// Tool access gating per tier
export const TOOL_ACCESS: Record<string, PlanTier> = {
  "nexis-ai": "free",
  "keyword-research": "free",
  "launch-advisor": "starter",
  "monetisation-ai": "creator",
  "clipping-ai": "creator",
  "email-list-builder": "pro",
  "sales-page-builder": "pro",
};

export const TIER_ORDER: Record<PlanTier, number> = {
  free: 0,
  starter: 1,
  creator: 2,
  pro: 3,
};

export const hasToolAccess = (userTier: PlanTier | "lifetime" | null, toolKey: string): boolean => {
  if (userTier === "lifetime") return true;
  if (!userTier) return false;
  const requiredTier = TOOL_ACCESS[toolKey];
  if (!requiredTier) return true;
  return TIER_ORDER[userTier] >= TIER_ORDER[requiredTier];
};

// Value stack for landing page
export const VALUE_STACK = [
  { name: "Monetisation AI", value: "$997/year" },
  { name: "Nexis AI", value: "$497/year" },
  { name: "Clipping AI", value: "$497/year" },
  { name: "Keyword Research AI", value: "$297/year" },
  { name: "Email List Builder AI", value: "$297/year" },
  { name: "Sales Page Builder AI", value: "$297/year" },
  { name: "Launch Advisor", value: "$197/year" },
];

export const TOTAL_VALUE = "$2,982/year";
